import { mapActions, mapState } from 'vuex';
import { get, findIndex } from 'lodash';

/**
 * @Mixin GridFormatDataMixin
 */
export default (config) => {
  const computedConfig = {
    computed: {
      isOneClientView() {
        return this.selectedClient !== 0 && this.selectedClientName !== 'All Clients';
      },
      statusesItemsName() {
        if (this.isOneClientView) {
          return {
            singular: 'fund',
            plural: 'funds',
          };
        }
        return {
          singular: 'client',
          plural: 'clients',
        };
      },
    },
  };

  if (config.withGlobalState) {
    computedConfig.computed = {
      ...mapState({
        policies: (state) => state.user.policies,
        selectedFiduciary: (state) => state.user.fiduciary.id,
        selectedClient: (state) => state.grid.client.selected,
        selectedProduct: (state) => state.grid.product.selected,
        selectedProductName: (state) => state.grid.product.name,
        selectedProductCode: (state) => state.grid.product.code,
        searchTerm: (state) => state.grid.searchTerm,
        selectedTab: (state) => state.grid.tab,
      }),
      ...computedConfig.computed,
    };
  }

  return {
    methods: {
      ...mapActions({
        logError: 'logError',
      }),
      isTargetSeries(fund) {
        const fundsSeries = get(fund, 'fundSeries.seriesFunds', null);
        return !!fundsSeries && Array.isArray(fundsSeries);
      },
      formatTargetSeriesStatus(rowData, currentData = [], pendingData = [], disableNone) {
        // If has pendingStatus override production Status
        const hasPendingData = Array.isArray(pendingData) && pendingData.length > 0;
        // In this case, we already have the pending statuses prioritized in the currentData array
        const data = this.isTargetSeries(rowData) || hasPendingData
          ? pendingData
          : currentData;

        const list = data.map((item) => ({
          id: get(item, 'fundStatus.id', 0),
          name: get(item, 'fundStatus.name', ''),
        }));

        // If the value returned here is an empty array this row won't be shown
        return this.formatToWeightedList(list, (el) => el.id !== 4 || disableNone === false);
      },
      formatPendingStatuses(data) {
        if (!(this.isOneClientView && this.isTargetSeries(data))) {
          return data.pendingStatus;
        }

        const fundSeries = get(data, 'fundSeries.seriesFunds') || [];
        let pendingStatuses = [];

        // This process is necessary because all the pending status come inside an array.
        // Then we have an array of fund pending statuses per fund series &
        // I need an array of status.
        fundSeries
          .filter((fund) => fund.pendingStatus !== null)
          .forEach(({ pendingStatus }) => {
            if (Array.isArray(pendingStatus)) {
              pendingStatuses = pendingStatuses.concat(pendingStatus);
            }
          });

        return pendingStatuses;
      },
      formatFundStatuses(data) {
        if (!(this.isOneClientView && this.isTargetSeries(data))) {
          return data.fundStatus;
        }

        const fundSeries = get(data, 'fundSeries.seriesFunds', []);
        let fundStatuses = [];

        // This process is necessary because all the fund status come inside an array.
        // Then we have an array of fund statuses per fund series & I need an array of status.
        fundSeries
          .filter((fund) => fund.fundStatus !== null || fund.pendingStatus !== null)
          .forEach(({ pendingStatus, fundStatus }) => {
            // Pending statuses have priority
            const statusToInclude = Array.isArray(pendingStatus) ? pendingStatus : fundStatus;

            fundStatuses = fundStatuses.concat(statusToInclude);
          });

        return fundStatuses;
      },
      formatToWeightedList(list = [], include, property = 'name') {
        // Group by ocurrence
        const weightedList = [];
        list.forEach((element) => {
          if (include(element)) {
            const index = findIndex(weightedList, [property, element[property]]);
            if (index === -1) {
              weightedList.push({
                [property]: element[property],
                id: element.id,
                value: 1,
              });
            } else {
              weightedList[index].value += 1;
              weightedList[index].id = element.id;
            }
          }
        });

        // Order by mayor ocurrence
        weightedList.sort((a, b) => {
          if (a.value > b.value) {
            return -1;
          }
          if (a.value < b.value) {
            return 1;
          }
          if (a.value === b.value && a[property].toLowerCase() < b[property].toLowerCase()) {
            return -1;
          }
          if (a.value === b.value && a[property].toLowerCase() > b[property].toLowerCase()) {
            return 1;
          }
          return 0;
        });

        return weightedList;
      },

      formatCategories(categories = []) {
        const list = (categories
            && Array.isArray(categories)
            && categories.map((item) => ({ name: get(item, 'category.name', '') })))
          || [];

        return this.formatToWeightedList(list, () => true);
      },
    },
    ...computedConfig,
  };
};
